import React, { useState, useEffect, useRef } from "react";
import { createGlobalStyle } from 'styled-components';
import axios from 'axios';

const GlobalStyle = createGlobalStyle`
    html, body {
        height: 100%;
        background-color: #182627;
    }

    #root {
        min-height: 100%;
    }

    .settings-container {
        max-width: 900px;
        margin: 0 auto;
        padding: 40px 20px;
        color: white;
    }

    .settings-container h1 {
        color: var(--plenoil-yellow);
        font-size: 24px;
        margin-bottom: 30px;
    }

    .section-card {
        background: var(--plenoil-blue);
        border: 1px solid var(--plenoil-blue-light);
        border-radius: 10px;
        padding: 20px;
        margin-bottom: 20px;
    }

    .section-card h2 {
        color: var(--plenoil-yellow);
        font-size: 18px;
        margin: 0 0 5px 0;
    }

    .section-card .section-desc {
        opacity: 0.6;
        font-size: 13px;
        margin-bottom: 15px;
    }

    .key-row {
        display: grid;
        grid-template-columns: 1fr 2fr auto;
        gap: 15px;
        align-items: center;
        padding: 10px 0;
        border-top: 1px solid rgba(255,255,255,0.08);
    }

    .key-row:first-of-type {
        border-top: none;
    }

    .key-row .label {
        font-weight: bold;
        font-size: 13px;
    }

    .key-row .label small {
        display: block;
        font-weight: normal;
        opacity: 0.5;
        font-size: 11px;
    }

    .key-row input[type="text"],
    .key-row input[type="password"],
    .key-row input[type="number"] {
        width: 100%;
        background: rgba(0,0,0,0.2);
        color: white;
        border: 1px solid rgba(255,255,255,0.15);
        padding: 6px 10px;
        border-radius: 4px;
        font-size: 13px;
    }

    .key-row .toggle {
        display: flex;
        align-items: center;
        gap: 6px;
        font-size: 12px;
        white-space: nowrap;
    }

    .key-row .toggle input {
        transform: scale(1.2);
    }

    .save-btn {
        background: var(--plenoil-orange);
        color: white;
        border: 0;
        padding: 10px 30px;
        border-radius: 30px;
        margin-top: 20px;
        font-size: 14px;
    }

    .login-card {
        max-width: 400px;
        margin: 100px auto;
        background: var(--plenoil-blue);
        padding: 30px;
        border-radius: 10px;
        color: white;
    }

    .login-card h1 {
        color: var(--plenoil-yellow);
        text-align: center;
        margin-bottom: 20px;
    }

    .login-card input {
        width: 100%;
        padding: 10px;
        background: rgba(0,0,0,0.2);
        border: 1px solid rgba(255,255,255,0.15);
        color: white;
        border-radius: 4px;
        margin-bottom: 10px;
    }

    .login-card .err {
        background: var(--plenoil-red);
        padding: 10px;
        border-radius: 4px;
        text-align: center;
        margin-bottom: 10px;
        font-size: 13px;
    }

    .global-disabled {
        background: var(--plenoil-red);
        padding: 15px;
        border-radius: 10px;
        text-align: center;
        margin-bottom: 20px;
    }
`;

const STORAGE_KEY = 'settings_override_password';

export default function SettingsOverride() {
    const [password, setPassword] = useState(sessionStorage.getItem(STORAGE_KEY) || '');
    const [authed, setAuthed] = useState(false);
    const [globalDisabled, setGlobalDisabled] = useState(false);
    const [sections, setSections] = useState([]);
    const [dirtyValues, setDirtyValues] = useState({});
    const [dirtyUseDb, setDirtyUseDb] = useState({});
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const passwordInputRef = useRef(null);

    useEffect(() => {
        if ( password ) {
            loadData(password);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadData = async (pwd) => {
        setError(null);
        try {
            const response = await axios.post('/api/settings-override/values', { master_password: pwd });
            setSections(response.data.sections || []);
            setAuthed(true);
            setGlobalDisabled(false);
            sessionStorage.setItem(STORAGE_KEY, pwd);
        } catch (e) {
            if ( e.response?.status === 403 ) {
                setGlobalDisabled(true);
                setAuthed(false);
                sessionStorage.removeItem(STORAGE_KEY);
            } else if ( e.response?.status === 401 ) {
                setError('Contraseña incorrecta');
                setAuthed(false);
                sessionStorage.removeItem(STORAGE_KEY);
            } else {
                setError('Error de conexión');
            }
        }
    };

    const doLogin = (e) => {
        e.preventDefault();
        const pwd = passwordInputRef.current.value;
        setPassword(pwd);
        loadData(pwd);
    };

    const onValueChange = (key, value) => {
        setDirtyValues({ ...dirtyValues, [key]: value });
    };

    const onUseDbChange = (key, checked) => {
        setDirtyUseDb({ ...dirtyUseDb, [key]: checked });
    };

    const save = async () => {
        setSaving(true);
        setError(null);

        const changes = [];
        const allKeys = new Set([...Object.keys(dirtyValues), ...Object.keys(dirtyUseDb)]);
        allKeys.forEach(key => {
            const change = { key };
            if ( key in dirtyValues ) change.value = dirtyValues[key];
            if ( key in dirtyUseDb ) change.use_db = dirtyUseDb[key];
            changes.push(change);
        });

        try {
            const response = await axios.put('/api/settings-override/values', {
                master_password: password,
                changes
            });
            setSections(response.data.sections || []);
            setDirtyValues({});
            setDirtyUseDb({});
        } catch (e) {
            setError('Error al guardar: ' + (e.response?.data?.message || e.message));
        } finally {
            setSaving(false);
        }
    };

    if ( globalDisabled ) {
        return (
            <React.Fragment>
                <GlobalStyle />
                <div className="settings-container">
                    <h1>Settings Override</h1>
                    <div className="global-disabled">
                        Esta funcionalidad está deshabilitada globalmente. Define <code>SETTINGS_OVERRIDE_ENABLED=true</code> en el <code>.env</code> para habilitarla.
                    </div>
                </div>
            </React.Fragment>
        );
    }

    if ( !authed ) {
        return (
            <React.Fragment>
                <GlobalStyle />
                <div className="login-card">
                    <h1>Settings Override</h1>
                    <form onSubmit={doLogin}>
                        {error && <div className="err">{error}</div>}
                        <input
                            ref={passwordInputRef}
                            type="password"
                            placeholder="Contraseña maestra"
                            autoFocus
                        />
                        <button className="save-btn" type="submit">Entrar</button>
                    </form>
                </div>
            </React.Fragment>
        );
    }

    const hasChanges = Object.keys(dirtyValues).length > 0 || Object.keys(dirtyUseDb).length > 0;

    return (
        <React.Fragment>
            <GlobalStyle />
            <div className="settings-container">
                <h1>Settings Override</h1>
                {error && <div className="global-disabled">{error}</div>}
                {sections.map(section => (
                    <div key={section.key} className="section-card">
                        <h2>{section.label}</h2>
                        {section.description && <div className="section-desc">{section.description}</div>}
                        {section.keys.map(k => {
                            const valueShown = (k.key in dirtyValues) ? dirtyValues[k.key] : (k.value ?? '');
                            const useDbShown = (k.key in dirtyUseDb) ? dirtyUseDb[k.key] : k.use_db;
                            return (
                                <div key={k.key} className="key-row">
                                    <div className="label">
                                        {k.label}
                                        <small>{k.key}</small>
                                        {k.description && <small>{k.description}</small>}
                                    </div>
                                    <input
                                        type={k.input_type === 'password' ? 'password' : (k.input_type === 'number' ? 'number' : 'text')}
                                        value={valueShown}
                                        onChange={(e) => onValueChange(k.key, e.target.value)}
                                        placeholder={useDbShown ? 'Valor en BD' : 'Se usa .env'}
                                    />
                                    <label className="toggle">
                                        <input
                                            type="checkbox"
                                            checked={!!useDbShown}
                                            onChange={(e) => onUseDbChange(k.key, e.target.checked)}
                                        />
                                        Usar BD
                                    </label>
                                </div>
                            );
                        })}
                    </div>
                ))}
                <button className="save-btn" onClick={save} disabled={!hasChanges || saving}>
                    {saving ? 'Guardando...' : 'Guardar cambios'}
                </button>
            </div>
        </React.Fragment>
    );
}
